import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWeb3 } from '../context/Web3Context';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { CountdownToLaunch } from '../components/CountdownToLaunch';
import { ShareCompletionModal } from '../components/ShareCompletionModal';
import { toast } from 'sonner';
import {
    Sparkles, Gift, Check, ExternalLink, Users, Coins, Trophy, Loader2,
    Lock, TrendingUp, Flame, Twitter, MessageCircle, Send as SendIcon,
    Target, Zap, Crown,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
    social: { color: '#1DA1F2', icon: Twitter, labelKey: 'airdrop.catSocial' },
    platform: { color: '#00FF66', icon: Target, labelKey: 'airdrop.catPlatform' },
    referral: { color: '#FFD700', icon: Users, labelKey: 'airdrop.catReferral' },
    token: { color: '#9B6FE8', icon: Coins, labelKey: 'airdrop.catToken' },
    meta: { color: '#FF6B35', icon: Trophy, labelKey: 'airdrop.catMeta' },
};

const ACTION_ICON = {
    follow_x: Twitter,
    join_discord: MessageCircle,
    join_telegram: SendIcon,
    tweet_b4p: Twitter,
};

function formatAddr(addr) {
    return addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : '';
}

function QuestCard({ quest, completed, onComplete, busy }) {
    const { t } = useTranslation();
    const cat = CATEGORY_META[quest.category] || CATEGORY_META.meta;
    const Icon = ACTION_ICON[quest.id] || cat.icon;

    async function handleClick() {
        if (completed || busy) return;
        if (!quest.auto && quest.action_url) {
            window.open(quest.action_url, '_blank', 'noopener');
            // Give user 4s to do the action before marking complete
            setTimeout(() => onComplete(quest.id), 4000);
        } else {
            onComplete(quest.id);
        }
    }

    return (
        <Card
            className={`${completed ? 'bg-[#00FF66]/10 border-[#00FF66]/30' : 'bg-white/[0.04] border-white/10 hover:border-[#FFD700]/40'} transition-all`}
            data-testid={`quest-${quest.id}`}
        >
            <CardContent className="p-4">
                <div className="flex items-start gap-3 mb-3">
                    <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${cat.color}22` }}
                    >
                        <Icon className="w-5 h-5" style={{ color: cat.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="font-bold text-white text-sm">{t(`airdrop.quest_${quest.id}`)}</span>
                            {quest.auto && (
                                <Badge className="text-[9px] bg-[#9B6FE8]/15 text-[#9B6FE8] border-0">
                                    {t('airdrop.autoVerify')}
                                </Badge>
                            )}
                        </div>
                        <div className="text-xs text-white/50">{t(`airdrop.quest_${quest.id}_desc`)}</div>
                    </div>
                </div>
                <div className="flex items-center justify-between">
                    <span className="font-mono text-[#FFD700] font-black text-lg">+{quest.reward.toLocaleString()} B4P</span>
                    {completed ? (
                        <Badge className="bg-[#00FF66]/15 text-[#00FF66] border-0">
                            <Check className="w-3 h-3 mr-1" /> {t('airdrop.completed')}
                        </Badge>
                    ) : (
                        <Button
                            size="sm"
                            onClick={handleClick}
                            disabled={busy}
                            className="bg-[#FFD700] text-[#001A4D] hover:bg-[#FFC700] font-semibold text-xs h-7"
                            data-testid={`quest-${quest.id}-btn`}
                        >
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : (
                                quest.auto ? t('airdrop.verify') : (
                                    <>{t('airdrop.go')} <ExternalLink className="w-3 h-3 ml-1" /></>
                                )
                            )}
                        </Button>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

function LockdropPanel({ lockdropConfig, lockdropUser, onDepositMade, address }) {
    const { t } = useTranslation();
    const [lockDays, setLockDays] = useState(90);
    const [amount, setAmount] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const { isConnected, connect } = useWeb3();

    const selectedOption = lockdropConfig?.lock_options?.find(o => o.days === lockDays);
    const bonusB4P = (parseFloat(amount) || 0) * (selectedOption?.bonus_b4p_per_eth || 0);
    const multiplierBoost = Math.min(
        lockdropConfig?.max_multiplier_boost || 3,
        ((parseFloat(amount) || 0) / 0.1) * (lockdropConfig?.multiplier_per_01_eth || 1),
    );

    async function handleLock() {
        if (!isConnected) { await connect(); return; }
        const n = parseFloat(amount);
        if (!n || n < (lockdropConfig?.min_deposit_eth || 0.01)) {
            toast.error(t('airdrop.lockMinError', { min: lockdropConfig?.min_deposit_eth }));
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(`${API}/api/lockdrop/deposit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    address,
                    amount_eth: n,
                    lock_days: lockDays,
                    tx_hash: `lock_${Date.now()}_${address.slice(2, 10)}`,
                }),
            });
            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();
            toast.success(t('airdrop.lockSuccess'), {
                description: `${n} ETH · ${lockDays}d · +${data.deposit.amount_eth * selectedOption.bonus_b4p_per_eth} B4P bonus`,
            });
            setAmount('');
            onDepositMade();
        } catch (e) {
            toast.error(t('airdrop.lockFailed'), { description: String(e).slice(0, 100) });
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Card className="bg-gradient-to-br from-[#0066CC]/15 to-white/[0.04] border-[#0066CC]/30" data-testid="airdrop-lockdrop-panel">
            <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Lock className="w-5 h-5 text-[#6FA8FF]" />
                    {t('airdrop.lockTitle')}
                </CardTitle>
                <p className="text-xs text-white/60">{t('airdrop.lockDesc')}</p>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Lock period selector */}
                <div className="grid grid-cols-3 gap-2">
                    {lockdropConfig?.lock_options?.map((opt) => (
                        <button
                            type="button"
                            key={opt.days}
                            onClick={() => setLockDays(opt.days)}
                            data-testid={`lock-option-${opt.days}`}
                            className={`p-2 rounded-lg border text-center transition-all ${
                                lockDays === opt.days
                                    ? 'bg-[#6FA8FF]/20 border-[#6FA8FF]/50 text-white'
                                    : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
                            }`}
                        >
                            <div className="font-mono text-sm font-bold">{opt.days}d</div>
                            <div className="text-[10px] text-[#FFD700]">+{opt.bonus_b4p_per_eth}/ETH</div>
                        </button>
                    ))}
                </div>

                {/* Amount input */}
                <div>
                    <Input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min={lockdropConfig?.min_deposit_eth || 0.01}
                        max={lockdropConfig?.max_deposit_per_wallet}
                        placeholder="0.0 ETH"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="bg-white/5 border-white/10 text-white font-mono"
                        data-testid="lock-amount-input"
                    />
                    <div className="text-xs text-white/40 mt-1">
                        {t('airdrop.lockReturned')}
                    </div>
                </div>

                {/* Preview */}
                <div className="p-3 rounded-lg bg-white/[0.04] border border-white/10 space-y-1 text-sm">
                    <div className="flex justify-between">
                        <span className="text-white/60">{t('airdrop.lockBonus')}</span>
                        <span className="font-mono text-[#FFD700] font-bold">+{bonusB4P.toLocaleString()} B4P</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-white/60">{t('airdrop.lockMult')}</span>
                        <span className="font-mono text-[#00FF66] font-bold">+{multiplierBoost.toFixed(2)}x</span>
                    </div>
                </div>

                <Button
                    onClick={handleLock}
                    disabled={submitting || !amount || parseFloat(amount) <= 0}
                    className="w-full bg-[#6FA8FF] text-[#001A4D] hover:bg-[#5090E0] font-bold"
                    data-testid="lock-deposit-btn"
                >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                    {t('airdrop.lockCta', { days: lockDays })}
                </Button>

                {/* My locked */}
                {lockdropUser && lockdropUser.total_eth_locked > 0 && (
                    <div className="p-3 rounded-lg bg-[#00FF66]/10 border border-[#00FF66]/30 text-sm">
                        <div className="flex justify-between">
                            <span className="text-white/60">{t('airdrop.lockMyLocked')}</span>
                            <span className="font-mono font-bold text-white">{lockdropUser.total_eth_locked} ETH</span>
                        </div>
                        <div className="flex justify-between text-xs mt-1">
                            <span className="text-white/40">{t('airdrop.lockMyBoost')}</span>
                            <span className="font-mono text-[#00FF66]">+{lockdropUser.multiplier_boost}x</span>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default function AirdropPage() {
    const { t } = useTranslation();
    const { address, isConnected, connect } = useWeb3();

    // Capture referral param from URL → persist in localStorage so it survives wallet connect & navigation
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const ref = params.get('ref');
        if (ref && /^0x[a-fA-F0-9]{40}$/.test(ref)) {
            const stored = localStorage.getItem('b4p_referrer');
            if (!stored) localStorage.setItem('b4p_referrer', ref.toLowerCase());
        }
    }, []);

    const [config, setConfig] = useState(null);
    const [stats, setStats] = useState(null);
    const [userInfo, setUserInfo] = useState(null);
    const [lockdropConfig, setLockdropConfig] = useState(null);
    const [lockdropUser, setLockdropUser] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [busyQuest, setBusyQuest] = useState(null);
    const [filter, setFilter] = useState('all');
    const [shareModal, setShareModal] = useState({ open: false, quest: null, reward: 0 });

    async function refresh() {
        try {
            const [cfg, st, lbR, ldCfg] = await Promise.all([
                fetch(`${API}/api/airdrop/config`).then(r => r.json()),
                fetch(`${API}/api/airdrop/stats`).then(r => r.json()),
                fetch(`${API}/api/airdrop/leaderboard?limit=20`).then(r => r.json()),
                fetch(`${API}/api/lockdrop/config`).then(r => r.json()),
            ]);
            setConfig(cfg);
            setStats(st);
            setLeaderboard(lbR.leaderboard || []);
            setLockdropConfig(ldCfg);
            if (address) {
                const [u, ld] = await Promise.all([
                    fetch(`${API}/api/airdrop/user/${address}`).then(r => r.json()),
                    fetch(`${API}/api/lockdrop/user/${address}`).then(r => r.json()),
                ]);
                setUserInfo(u);
                setLockdropUser(ld);
            }
        } catch (e) {
            console.error('airdrop fetch failed', e);
        }
    }

    useEffect(() => {
        refresh();
        const id = setInterval(refresh, 30_000);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [address]);

    async function completeQuest(questId) {
        if (!isConnected) { await connect(); return; }
        setBusyQuest(questId);
        try {
            const res = await fetch(`${API}/api/airdrop/complete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ address, quest_id: questId }),
            });
            const data = await res.json();
            if (data.status === 'ok') {
                toast.success(t('airdrop.questCompleted'), {
                    description: `+${data.completion.reward} B4P`,
                });
                // Open viral share modal — fetch fresh user data first so totals reflect the new quest
                const fresh = await fetch(`${API}/api/airdrop/user/${address}`).then(r => r.json());
                setUserInfo(fresh);
                refresh();
                setShareModal({
                    open: true,
                    quest: config?.quests?.find(q => q.id === questId),
                    reward: data.completion.reward,
                });
            } else if (data.status === 'already_completed') {
                toast.info(t('airdrop.questAlready'));
            } else if (data.status === 'not_eligible') {
                toast.warning(t('airdrop.questNotEligible'), { description: data.message });
            }
        } catch (e) {
            toast.error('Error', { description: String(e).slice(0, 100) });
        } finally {
            setBusyQuest(null);
        }
    }

    const allCategories = ['all', 'social', 'platform', 'referral', 'token', 'meta'];
    const completedQuestIds = new Set((userInfo?.quests || []).filter(q => q.completed).map(q => q.id));
    const filteredQuests = (config?.quests || []).filter(q => filter === 'all' || q.category === filter);
    const userMultiplier = userInfo?.multiplier || 1;
    const userFinal = userInfo?.final_b4p || 0;
    const userBase = userInfo?.base_b4p || 0;
    const progress = userInfo ? (userBase / userInfo.base_cap) * 100 : 0;

    return (
        <div className="min-h-screen" data-testid="airdrop-page">
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-white/10">
                <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/10 via-[#FF6B35]/5 to-[#0066CC]/15" />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-14 md:py-20 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FFD700]/15 border border-[#FFD700]/30 mb-6">
                        <Gift className="w-3.5 h-3.5 text-[#FFD700]" />
                        <span className="text-[#FFD700] text-xs font-bold uppercase tracking-widest">
                            {t('airdrop.heroBadge')}
                        </span>
                    </div>
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-3">
                        <span className="text-[#FFD700]">150M B4P</span> {t('airdrop.heroTitle')}
                    </h1>
                    <p className="text-lg text-white/70 max-w-2xl mx-auto mb-8">
                        {t('airdrop.heroDesc')}
                    </p>
                    <CountdownToLaunch />
                </div>
            </section>

            {/* My Airdrop Card */}
            {isConnected && userInfo && (
                <section className="py-8 border-b border-white/10 bg-white/[0.02]">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6">
                        <Card className="bg-gradient-to-br from-[#FFD700]/10 via-white/[0.04] to-[#9B6FE8]/10 border-[#FFD700]/30" data-testid="airdrop-user-card">
                            <CardContent className="p-6">
                                <div className="flex flex-col md:flex-row gap-6 items-center justify-between">
                                    <div>
                                        <div className="text-xs text-[#FFD700] uppercase tracking-wider mb-1">{t('airdrop.myAirdrop')}</div>
                                        <div className="font-mono text-5xl font-black text-white tabular-nums">
                                            {userFinal.toLocaleString()} <span className="text-2xl text-[#FFD700]">B4P</span>
                                        </div>
                                        <div className="text-xs text-white/50 mt-1 font-mono">
                                            {userBase.toLocaleString()} {t('airdrop.basePoints')} × {userMultiplier}x = {userFinal.toLocaleString()}
                                        </div>
                                    </div>
                                    <div className="flex-1 max-w-md w-full">
                                        <div className="flex justify-between text-xs text-white/60 mb-2">
                                            <span>{t('airdrop.progressToCap')}</span>
                                            <span className="font-mono">{Math.min(100, progress).toFixed(0)}%</span>
                                        </div>
                                        <Progress value={Math.min(100, progress)} className="h-3 bg-white/10" data-testid="airdrop-progress" />
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            {Object.entries(userInfo.multiplier_breakdown).map(([k, v]) => {
                                                if (v === 0 || k === 'base') return null;
                                                return (
                                                    <Badge key={k} className="bg-white/10 text-white text-[10px] border-0" data-testid={`mult-badge-${k}`}>
                                                        +{v.toFixed(2)}x {t(`airdrop.boost_${k}`)}
                                                    </Badge>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </section>
            )}

            {/* Global Stats */}
            <section className="py-10 border-b border-white/10">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Card className="bg-white/[0.04] border-white/10" data-testid="airdrop-stat-budget">
                            <CardContent className="p-4">
                                <Coins className="w-4 h-4 text-[#FFD700] mb-2" />
                                <div className="font-mono text-2xl font-black text-white">150M</div>
                                <div className="text-[10px] text-white/50 uppercase">{t('airdrop.statBudget')}</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-white/[0.04] border-white/10" data-testid="airdrop-stat-participants">
                            <CardContent className="p-4">
                                <Users className="w-4 h-4 text-[#00FF66] mb-2" />
                                <div className="font-mono text-2xl font-black text-white">{stats?.participants || 0}</div>
                                <div className="text-[10px] text-white/50 uppercase">{t('airdrop.statParticipants')}</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-white/[0.04] border-white/10" data-testid="airdrop-stat-claimed">
                            <CardContent className="p-4">
                                <Sparkles className="w-4 h-4 text-[#9B6FE8] mb-2" />
                                <div className="font-mono text-2xl font-black text-white">
                                    {((stats?.raw_b4p_earned || 0) / 1_000_000).toFixed(2)}M
                                </div>
                                <div className="text-[10px] text-white/50 uppercase">{t('airdrop.statClaimed')}</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-white/[0.04] border-white/10" data-testid="airdrop-stat-completions">
                            <CardContent className="p-4">
                                <Check className="w-4 h-4 text-[#FF6B35] mb-2" />
                                <div className="font-mono text-2xl font-black text-white">{stats?.total_completions || 0}</div>
                                <div className="text-[10px] text-white/50 uppercase">{t('airdrop.statCompletions')}</div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* MAIN GRID */}
            <section className="py-12">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Quest area */}
                    <div className="lg:col-span-2 space-y-4">
                        <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                            {allCategories.map((c) => (
                                <button
                                    type="button"
                                    key={c}
                                    onClick={() => setFilter(c)}
                                    data-testid={`airdrop-filter-${c}`}
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                                        filter === c
                                            ? 'bg-[#FFD700] text-[#001A4D]'
                                            : 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10'
                                    }`}
                                >
                                    {c === 'all' ? t('airdrop.filterAll') : t(CATEGORY_META[c].labelKey)}
                                </button>
                            ))}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {filteredQuests.map((q) => (
                                <QuestCard
                                    key={q.id}
                                    quest={q}
                                    completed={completedQuestIds.has(q.id)}
                                    busy={busyQuest === q.id}
                                    onComplete={completeQuest}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Right column: Lockdrop + Leaderboard */}
                    <div className="space-y-6">
                        <LockdropPanel
                            lockdropConfig={lockdropConfig}
                            lockdropUser={lockdropUser}
                            onDepositMade={refresh}
                            address={address}
                        />

                        <Card className="bg-white/[0.02] border-white/10" data-testid="airdrop-leaderboard">
                            <CardHeader>
                                <CardTitle className="text-base text-white flex items-center gap-2">
                                    <Crown className="w-4 h-4 text-[#FFD700]" />
                                    {t('airdrop.lbTitle')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                {leaderboard.length === 0 ? (
                                    <div className="text-sm text-white/50 text-center py-8">{t('airdrop.lbEmpty')}</div>
                                ) : (
                                    leaderboard.slice(0, 10).map((row) => {
                                        const isMe = address && row.address.toLowerCase() === address.toLowerCase();
                                        return (
                                            <div
                                                key={row.address}
                                                className={`p-2 rounded-lg flex items-center justify-between gap-2 ${isMe ? 'bg-[#00FF66]/10 border border-[#00FF66]/30' : 'bg-white/[0.03]'}`}
                                            >
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className="font-mono text-xs text-[#FFD700] w-6">#{row.rank}</span>
                                                    <span className="font-mono text-xs text-white truncate">{formatAddr(row.address)}</span>
                                                    {isMe && <Badge className="bg-[#00FF66]/20 text-[#00FF66] text-[9px] border-0 px-1">{t('common.you')}</Badge>}
                                                </div>
                                                <div className="text-right">
                                                    <div className="font-mono text-xs font-bold text-white">{row.final_b4p.toLocaleString()}</div>
                                                    <div className="text-[9px] text-white/40">×{row.multiplier}</div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-14 border-t border-white/10 bg-gradient-to-br from-[#FFD700]/5 to-transparent">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
                    <Zap className="w-10 h-10 text-[#FFD700] mx-auto mb-3" />
                    <h2 className="text-3xl sm:text-4xl font-black text-white mb-3">{t('airdrop.finalCtaTitle')}</h2>
                    <p className="text-white/60 mb-8">{t('airdrop.finalCtaDesc')}</p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Link to="/presale">
                            <Button size="lg" className="bg-[#FFD700] text-[#001A4D] hover:bg-[#FFC700] font-bold px-8 py-6">
                                <Flame className="w-4 h-4 mr-2" />
                                {t('airdrop.ctaPresale')}
                            </Button>
                        </Link>
                        <Link to="/b4p-launch">
                            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/5 px-8 py-6">
                                <TrendingUp className="w-4 h-4 mr-2" />
                                {t('airdrop.ctaLaunch')}
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            <ShareCompletionModal
                open={shareModal.open}
                onOpenChange={(o) => setShareModal((s) => ({ ...s, open: o }))}
                quest={shareModal.quest}
                reward={shareModal.reward}
                totalB4P={userInfo?.final_b4p || 0}
                multiplier={userInfo?.multiplier || 1}
                address={address}
            />
        </div>
    );
}
